// eMoney dependency rules — the single source of truth for which PQ entries
// eMoney needs before planning can build a plan. Two kinds:
//   static  — a fixed store path (family.client1DOB), optionally spouse-gated.
//   per-row — a column that every populated row of a table must carry
//             (e.g. Market Value + Ownership on each investment account).
//
// Everything reads from here: the PQ surfaces a blue dot on flagged fields /
// columns (FYI only, no gating), and the Plan Order screen lists every unmet
// dependency and blocks Send to Planning until they're filled. This set mirrors
// Srivani's Salesforce-side validation — tune it here to keep the two in sync.

const isBlank = v => v == null || String(v).trim() === '';

export function getAtPath(obj, path) {
  return String(path).split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

// Columns required on every populated row of an investment/asset table.
const ACCOUNT_COLS = [
  { key: 'marketValue', label: 'Market Value', type: 'currency' },
  { key: 'ownership', label: 'Ownership', type: 'picker' },
];
const INCOME_COLS = [
  { key: 'owner', label: 'Owner', type: 'picker' },
  { key: 'annualAmount', label: 'Annual Amount', type: 'currency' },
];

// arrayPath is `${sectionId}.${tableKey}` — the exact store path DataTable uses.
const ROW_RULES = [
  { arrayPath: 'assets.taxDeferred', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.roth', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.taxable', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.cashCd', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.hsa', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.plan529', section: 'Assets', cols: [
    { key: 'marketValue', label: 'Market Value', type: 'currency' },
    { key: 'ownership', label: 'Owner', type: 'picker' },
    { key: 'beneficiary', label: 'FBO', type: 'picker' },
  ] },
  { arrayPath: 'assets.realEstate', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'assets.businessOther', section: 'Assets', cols: ACCOUNT_COLS },
  { arrayPath: 'income.employment', section: 'Income', cols: INCOME_COLS },
  { arrayPath: 'income.socialSecurity', section: 'Income', cols: INCOME_COLS },
  { arrayPath: 'income.pension', section: 'Income', cols: INCOME_COLS },
  { arrayPath: 'income.other', section: 'Income', cols: INCOME_COLS },
  { arrayPath: 'liabilities.items', section: 'Liabilities', cols: [
    { key: 'amount', label: 'Amount', type: 'currency' },
  ] },
  { arrayPath: 'insurance.policies', section: 'Insurance', cols: [
    { key: 'type', label: 'Type', type: 'select', options: ['Term Life', 'Whole Life', 'Universal Life', 'LTC', 'Disability', 'Umbrella/Liability'] },
    { key: 'insured', label: 'Insured', type: 'picker' },
    { key: 'owner', label: 'Owner', type: 'picker' },
  ] },
];

const STATIC_RULES = [
  { path: 'family.client1DOB', section: 'Household', personKey: 'client1',
    fieldDef: { key: 'client1DOB', label: 'Date of Birth', type: 'date', dateRange: 'birthdate' } },
  { path: 'family.client2DOB', section: 'Household', personKey: 'client2', flag: 'hasSpouse',
    fieldDef: { key: 'client2DOB', label: 'Date of Birth', type: 'date', dateRange: 'birthdate' } },
];

const fullName = (first, last) =>
  [String(first ?? '').trim(), String(last ?? '').trim()].filter(Boolean).join(' ');

// Disambiguate the two DOBs by person, but only when a spouse exists.
function personLabelFor(rule, data) {
  if (!data?._flags?.hasSpouse) return '';
  const fam = data.family || {};
  if (rule.personKey === 'client1') return fullName(fam.client1FirstName, fam.client1LastName) || 'Client';
  if (rule.personKey === 'client2') return fullName(fam.client2FirstName, fam.client2LastName) || 'Spouse';
  return '';
}

// A row "exists" (and so must satisfy its columns) once it carries any real
// user data — a just-added empty row doesn't demand anything yet.
function rowHasData(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.entries(row).some(([k, v]) => !k.startsWith('_') && !isBlank(v));
}

const rowUidOf = row => row._uid || row._autoKey || row._source || row._reKey || '';
const rowName = row =>
  String(row?.custodian || row?.description || row?.company || row?.name || row?.owner || '').trim();

// The full requirement list for this household, each stamped with `missing`.
// Static rules expand to one entry; per-row rules expand to one entry per
// populated row × required column. Read-only (auto-derived) columns are skipped
// — the advisor can't fill an employment-income row's derived owner/amount.
export function resolveEmoneyRequirements(data = {}) {
  const flags = data._flags || {};
  const out = [];

  for (const r of STATIC_RULES) {
    if (r.flag && !flags[r.flag]) continue;
    out.push({
      id: r.path, kind: 'field', path: r.path, section: r.section,
      fieldDef: r.fieldDef, label: r.fieldDef.label,
      personLabel: personLabelFor(r, data),
      missing: isBlank(getAtPath(data, r.path)),
    });
  }

  for (const rule of ROW_RULES) {
    const arr = getAtPath(data, rule.arrayPath);
    if (!Array.isArray(arr)) continue;
    arr.forEach(row => {
      if (!rowHasData(row)) return;
      const uid = rowUidOf(row);
      const nm = rowName(row);
      for (const col of rule.cols) {
        if (Array.isArray(row._readOnly) && row._readOnly.includes(col.key)) continue;
        out.push({
          id: `${rule.arrayPath}:${uid}:${col.key}`, kind: 'row',
          arrayPath: rule.arrayPath, rowUid: uid, colKey: col.key,
          section: rule.section, fieldDef: col,
          label: nm ? `${col.label} — ${nm}` : col.label,
          missing: isBlank(row[col.key]),
        });
      }
    });
  }

  return out;
}

export function getMissingEmoneyFields(data = {}) {
  return resolveEmoneyRequirements(data).filter(r => r.missing);
}

// Rule descriptors (not instances) — used only to answer "is the gate active?"
// (always true now that per-row rules exist).
export function getEmoneyRequirements() {
  return [...STATIC_RULES, ...ROW_RULES];
}

// Which column keys of a given store path are eMoney-required — drives the
// blue dot on DataTable column headers.
const EMPTY = new Set();
export function emoneyColumnKeys(path) {
  const rule = ROW_RULES.find(r => r.arrayPath === path);
  return rule ? new Set(rule.cols.map(c => c.key)) : EMPTY;
}
